import { VerticalBox } from "../../../common/box/VerticalBox.tsx";
import { Box, Typography } from "@mui/material";
import i18n from "i18next";
import React, { Fragment } from "react";
import { dayJsToDateIsoStringNullable, formatIsoString } from "src/utils/dayjsUtils.ts";
import { AavoDatePicker } from "src/components/common/inputFields/AavoDatePicker.tsx";
import type { AavoDatePickerProps } from "src/components/common/inputFields/AavoDatePicker.tsx";
import dayjs from "dayjs";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import type { AavoTextFieldProps } from "src/components/common/inputFields/AavoTextField.tsx";
import { ConditionalNullable } from "src/components/common/inputFields/types.ts";
import { getTaskStateLabel } from "src/api/generated/io/aavo/applications/db/postgres/enums/taskState.ts";
import { AsyncSelectField } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { UserApi } from "src/api/generated/users/api/userApi.ts";
import { AppUser } from "src/api/generated/postgres/db/types/role_management/tables/appUser.ts";
import { TaskWithUserNames } from "../types.ts";
import { PickLocationButton } from "src/components/views/tasks/location/PickLocationButton.tsx";
import { TaskLocation } from "src/components/views/tasks/location/TaskLocationPickerDialog.tsx";
import { UseAavoFormReturn } from "src/components/common/forms/useAavoForm/types.ts";
import { FieldPath, useWatch } from "react-hook-form";

export interface TaskMobileFormProps
	extends Pick<UseAavoFormReturn<TaskWithUserNames, unknown>, "control" | "setValue" | "getValues"> {
	isEditing: boolean;
	autoFocusField: keyof TaskWithUserNames | null;
	onStartEdit: (fieldName: keyof TaskWithUserNames) => void;
	onLocationSaved: (location: TaskLocation | null) => Promise<void>;
}

export const TaskMobileForm = (props: TaskMobileFormProps) => {
	const { control, setValue, getValues, onLocationSaved } = props;

	return (
		<VerticalBox
			sx={{
				flex: 1,
				overflow: "auto",
			}}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "auto 1fr",
					alignItems: "start",
					rowGap: 1.5,
					columnGap: 2,
					padding: 1,
					minHeight: "min-content",
					fontSize: "1.125rem",
				}}
			>
				<TaskField
					name={"title"}
					label={""}
					vertical
					renderEditField={(params) => (
						<TextFieldEditor {...params} />
					)}
					{...props}
				/>
				<TaskField
					name={"sourceDescription"}
					label={i18n.t("source")}
					vertical
					{...props}
				/>
				<TaskField
					name={"taskState"}
					label={i18n.t("state")}
					format={(value) => getTaskStateLabel(value)}
					{...props}
				/>
				<TaskField
					name={"assignedToUserId"}
					label={i18n.t("executor")}
					format={() => getValues("assignedToUserName") ?? "-"}
					renderEditField={(params) => (
						<UserFieldEditor
							userLabelField={"assignedToUserName"}
							clearable={getValues("releasedDate") == null}
							{...params}
						/>
					)}
					{...props}
				/>
				<TaskField
					name={"responsiblePersonUserId"}
					label={i18n.t("responsible_person")}
					format={() => getValues("responsiblePersonUserName") ?? "-"}
					renderEditField={({ onSubmit, ...other }) => (
						<UserFieldEditor
							clearable={false}
							userLabelField={"responsiblePersonUserName"}
							onSubmit={(newValue) => {
								if (newValue != null) {
									onSubmit(newValue);
								}
							}}
							{...other}
						/>
					)}
					{...props}
				/>
				<TaskField
					name={"earliestStartDate"}
					label={i18n.t("earliest_start_date")}
					format={formatIsoString}
					renderEditField={({ value, ...other }) => (
						<DateFieldEditor value={value ?? null} {...other} />
					)}
					{...props}
				/>
				<TaskField
					name={"deadlineDate"}
					label={i18n.t("deadline_date_shortened")}
					format={formatIsoString}
					renderEditField={(params) => <DateFieldEditor disableClearable {...params} />}
					{...props}
				/>
				<TaskField
					name={"workloadEstimateHours"}
					label={i18n.t("workload_estimate")}
					format={(value) => `${value ?? "-"} h`}
					renderEditField={({ value, onSubmit, autoFocus }) => (
						<TextFieldEditor
							type={"number"}
							value={value?.toString() ?? ""}
							onSubmit={(newValue) => onSubmit(Number(newValue))}
							autoFocus={autoFocus}
						/>
					)}
					{...props}
				/>
				<TaskField
					name={"taskDescription"}
					label={i18n.t("description")}
					vertical
					renderEditField={(params) => <TextFieldEditor {...params} multiline />}
					{...props}
				/>
				<TaskField
					name={"note"}
					label={i18n.t("comment")}
					vertical
					renderEditField={(params) => <TextFieldEditor {...params} multiline />}
					{...props}
				/>
				<LocationSection
					control={control}
					setValue={setValue}
					onLocationSaved={onLocationSaved}
				/>
				<TaskField
					name={"createdAt"}
					label={i18n.t("created_at")}
					format={formatIsoString}
					{...props}
				/>
				<TaskField
					name={"releasedDate"}
					label={i18n.t("released_at")}
					format={formatIsoString}
					hideIfNull
					{...props}
				/>
				<TaskField
					name={"actualStartDate"}
					label={i18n.t("started_at")}
					format={formatIsoString}
					hideIfNull
					{...props}
				/>
				<TaskField
					name={"completedDate"}
					label={i18n.t("completed_at")}
					format={formatIsoString}
					hideIfNull
					{...props}
				/>
				<TaskField
					name={"cancelledDate"}
					label={i18n.t("cancelled_at")}
					format={formatIsoString}
					hideIfNull
					{...props}
				/>
			</Box>
		</VerticalBox>
	);
};

interface LocationSectionProps
	extends Pick<TaskMobileFormProps, "control" | "setValue" | "onLocationSaved"> {}

const LocationSection = ({ control, setValue, onLocationSaved }: LocationSectionProps) => {
	const [locationLatitude, locationLongitude] = useWatch({
		control,
		name: ["locationLatitude", "locationLongitude"],
	});

	return (
		<VerticalBox
			sx={{
				gridColumn: "1 / -1",
			}}
		>
			<Typography
				sx={{
					color: "primary.main",
					fontWeight: "bold",
					fontSize: "inherit",
				}}
			>
				{i18n.t("location")}
			</Typography>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<PickLocationButton
					initialLocation={
						locationLatitude != null && locationLongitude != null
							? {
									latitude: locationLatitude,
									longitude: locationLongitude,
								}
							: undefined
					}
					onLocationPicked={onLocationPicked}
				/>
			</Box>
		</VerticalBox>
	);

	async function onLocationPicked(location: TaskLocation | null) {
		setValue("locationLatitude", location?.latitude ?? null);
		setValue("locationLongitude", location?.longitude ?? null);
		await onLocationSaved(location);
	}
};

interface TaskFieldProps<TName extends keyof TaskWithUserNames>
	extends Pick<TaskMobileFormProps, "control" | "setValue" | "getValues" | "isEditing" | "onStartEdit" | "autoFocusField"> {
	name: TName;
	label: string;
	format?: (value: NonNullable<TaskWithUserNames[TName]>) => string;
	renderEditField?: (params: RenderFieldParams<TaskWithUserNames[TName]>) => React.ReactNode;
	hideIfNull?: boolean;
	vertical?: boolean;
}

interface RenderFieldParams<TValue> {
	value: Exclude<TValue, undefined>;
	onSubmit: (newValue: TValue, otherUpdates?: Partial<TaskWithUserNames>) => void;
	autoFocus: boolean;
}

const TaskField = <TName extends keyof TaskWithUserNames>({
	control,
	setValue,
	isEditing,
	onStartEdit,
	autoFocusField,
	name,
	label,
	renderEditField,
	hideIfNull,
	vertical,
	format: formatProp,
}: TaskFieldProps<TName>) => {
	const value = useWatch({ control, name: name as FieldPath<TaskWithUserNames> }) as TaskWithUserNames[TName];
	const isEditable = renderEditField !== undefined;

	if (hideIfNull && value === null) return null;

	const labelTypography = (
		<Typography
			sx={{
				color: "primary.main",
				fontWeight: "bold",
				fontSize: "inherit",
			}}
		>
			{label}
		</Typography>
	);

	const showEditField = isEditing && isEditable;

	const content = showEditField
		? renderEditField({
				value: (value === undefined ? null : value) as Exclude<
					TaskWithUserNames[TName],
					undefined
				>,
				onSubmit: onSubmit,
				autoFocus: name === autoFocusField,
			})
		: (
			<Typography
				sx={{
					placeSelf: "stretch",
					whiteSpace: "pre-line",
					fontSize: "inherit",
				}}
				onClick={isEditable ? () => onStartEdit(name) : undefined}
			>
				{format(value)}
			</Typography>
		);

	if (vertical)
		return (
			<VerticalBox
				sx={{
					gridColumn: "1 / -1",
				}}
			>
				{labelTypography}
				{content}
			</VerticalBox>
		);
	else
		return (
			<Fragment>
				{labelTypography}
				{content}
			</Fragment>
		);

	function onSubmit(
		newValue: TaskWithUserNames[TName],
		otherUpdates?: Partial<TaskWithUserNames>,
	) {
		if (newValue === value) return;
		setValue(name as FieldPath<TaskWithUserNames>, newValue as never, { shouldDirty: true });
		if (otherUpdates) {
			for (const [key, val] of Object.entries(otherUpdates)) {
				setValue(key as FieldPath<TaskWithUserNames>, val as never, { shouldDirty: true });
			}
		}
	}

	function format(value: TaskWithUserNames[TName]) {
		if (value === null || value === undefined || value === "") return "-";
		if (formatProp) return formatProp(value);
		return String(value);
	}
};

interface TextFieldEditorProps
	extends Pick<AavoTextFieldProps, "type" | "multiline">,
		Omit<RenderFieldParams<string>, "value"> {
	value: string | null;
}

const TextFieldEditor = ({ value, onSubmit, autoFocus, ...other }: TextFieldEditorProps) => {
	return (
		<AavoTextField
			defaultValue={value ?? ""}
			onChange={(newVal) => onSubmit(newVal)}
			autoFocus={autoFocus}
			submitOnBlur={false}
			variant="standard"
			slotProps={{
				input: {
					sx: {
						padding: 0,
						fontSize: "inherit",
					},
				},
			}}
			{...other}
		/>
	);
};

interface DateFieldEditorProps<DisableClearable extends boolean | undefined>
	extends Omit<AavoDatePickerProps<DisableClearable>, "value">,
		Omit<RenderFieldParams<ConditionalNullable<string, DisableClearable>>, "autoFocus"> {}

const DateFieldEditor = <DisableClearable extends boolean | undefined>({
	value,
	onSubmit,
	...other
}: DateFieldEditorProps<DisableClearable>) => {
	return (
		<AavoDatePicker
			defaultValue={dayjs(value)}
			onChange={(date) => {
				onSubmit(
					dayJsToDateIsoStringNullable(date) as ConditionalNullable<string, DisableClearable>,
				);
			}}
			slotProps={{
				textField: {
					variant: "standard",
					sx: {
						"& input": {
							paddingY: 0,
						},
					},
				},
			}}
			{...other}
		/>
	);
};

interface UserFieldEditorProps extends RenderFieldParams<number | null> {
	clearable?: boolean;
	userLabelField: keyof TaskWithUserNames;
}

const UserFieldEditor = ({
	clearable = true,
	value,
	onSubmit,
	userLabelField,
}: UserFieldEditorProps) => {
	return (
		<AsyncSelectField<AppUser, number>
			label={""}
			defaultValue={value}
			showClearButton={clearable}
			onChange={(newUser) => {
				onSubmit(newUser?.id ?? null, { [userLabelField]: newUser?.name });
			}}
			fetchOptions={({ searchQuery, currentSelection }) =>
				UserApi.getUserSelectionOptions({
					searchQuery: searchQuery,
					currentSelectionUserId: currentSelection,
				})
			}
			getOptionKey={(option) => option.id}
			getOptionLabel={(option) => option.name}
			TextFieldProps={{
				variant: "standard",
				slotProps: {
					input: {
						style: {
							fontSize: "inherit",
						},
					},
					htmlInput: {
						style: {
							paddingTop: 0,
							paddingBottom: 0,
						},
					},
				},
			}}
		/>
	);
};
